import {ObjectTypeId} from '../../../constants';
import {IObjectData, IOverride} from '../../../index';
import {getRawSyncFieldValue} from '../../../util/misc';
import PromiseRequest from '../../../req/PromiseRequest';
import {nsGroupMemberOverride} from '../../group/members/nsGroupMemberOverride';
import buildAddOverrideForMember = nsGroupMemberOverride.buildAddOverrideForMember;
import invalidateMembershipOverrideCaches = nsGroupMemberOverride.invalidateMembershipOverrideCaches;
import {nsOverride} from '../../override/nsOverride';
import getOverrideStatusPillVariant = nsOverride.getOverrideStatusPillVariant;
import getOverrideStatusSuffix = nsOverride.getOverrideStatusSuffix;
import TOverrideArPillVariant = nsOverride.TOverrideArPillVariant;

export namespace nsUserMembership {

    /** Ascending override-column order (no-override rows use `NO_OVERRIDE_SORT_RANK`). */
    export const MEMBERSHIP_OVERRIDE_SORT_RANK: Readonly<Record<string, number>> = {
        'add.pending': 0,
        'add.synced': 1,
        'add.delete.pending': 2,
        'add.delete.synced': 3,
        'remove.pending': 4,
        'remove.synced': 5,
        'remove.delete.pending': 6,
        'remove.delete.synced': 7,
    };

    export const NO_OVERRIDE_SORT_RANK = 9;
    export const UNKNOWN_OVERRIDE_SORT_RANK = 8;

    export interface IMembershipOverrideDisplay {
        translationKey: string;
        pillVariant: TOverrideArPillVariant;
    }

    /** Overrides for this user’s group memberships (`object` = group, `target` = user). */
    export function filterRelevantMembershipOverrides(
        overrides: IOverride[],
        userId: number,
    ): IOverride[] {
        return (overrides ?? []).filter(o =>
            o.objecttypeid === ObjectTypeId.Group && o.targetobjectid === userId,
        );
    }

    /** Match a membership override to a group row. */
    export function findOverrideForGroup(overrides: IOverride[], group: IObjectData): IOverride | null {
        if (!overrides?.length || !group) {
            return null;
        }
        const groupSourceId = getRawSyncFieldValue('SourceId', group.syncfields);
        return overrides.find(o =>
            o.objecttypeid === ObjectTypeId.Group
            && (o.objectid === group.id
                || (!!groupSourceId && o.objectsrc === groupSourceId))
        ) ?? null;
    }

    /** Whether a group is already listed via synced `memberof`. */
    export function isGroupInMemberof(memberof: IObjectData[], group: IObjectData): boolean {
        if (!group?.id) {
            return false;
        }
        const groupSourceId = getRawSyncFieldValue('SourceId', group.syncfields);
        return (memberof ?? []).some(g =>
            g.id === group.id
            || (!!groupSourceId && getRawSyncFieldValue('SourceId', g.syncfields) === groupSourceId),
        );
    }

    /** Whether an override’s group is already covered by `memberof`. */
    export function isOverrideGroupInMemberof(memberof: IObjectData[], override: IOverride): boolean {
        return (memberof ?? []).some(g =>
            g.id === override.objectid
            || (!!override.objectsrc
                && getRawSyncFieldValue('SourceId', g.syncfields) === override.objectsrc),
        );
    }

    /** `objectsrc` values for override-only groups (not already in `memberof`). */
    export function collectOverrideGroupSourceIds(
        overrides: IOverride[],
        memberof: IObjectData[],
    ): string[] {
        const ids = new Set<string>();
        for (const o of overrides) {
            if (!o.objectsrc || isOverrideGroupInMemberof(memberof, o)) {
                continue;
            }
            ids.add(o.objectsrc);
        }
        return Array.from(ids);
    }

    /** Append override-only groups after synced `memberof`. */
    export function mergeMembershipGroups(
        memberof: IObjectData[],
        extraGroups: IObjectData[],
    ): IObjectData[] {
        const base = memberof ?? [];
        const extra = (extraGroups ?? []).filter(g => !isGroupInMemberof(base, g));
        return [...base, ...extra];
    }

    /** `user.membership.override.*` translation key and pill styling. */
    export function getUserMembershipOverrideDisplay(override: IOverride): IMembershipOverrideDisplay {
        return {
            translationKey: `user.membership.override.${getOverrideStatusSuffix(override)}`,
            pillVariant: getOverrideStatusPillVariant(override),
        };
    }

    export interface IMembershipAddGroupFailure {
        group: IObjectData;
        error: string;
    }

    export interface IMembershipAddGroupsBatchCallbacks {
        onProgress: (processed: number, total: number) => void;
        onComplete: (successes: IObjectData[], failures: IMembershipAddGroupFailure[]) => void;
    }

    /** Sequential `createoverride` per group (add membership for one user). */
    export function runMembershipAddGroupsBatch(
        req: PromiseRequest,
        user: IObjectData,
        groups: IObjectData[],
        callbacks: IMembershipAddGroupsBatchCallbacks,
    ): void {
        if (!groups.length) {
            callbacks.onComplete([], []);
            return;
        }
        const total = groups.length;
        const successes: IObjectData[] = [];
        const failures: IMembershipAddGroupFailure[] = [];
        let index = 0;

        const processNext = (): Promise<void> => {
            if (index >= groups.length) {
                return Promise.resolve();
            }
            const group = groups[index++];
            if (!group) {
                return processNext();
            }
            return req.request({
                joverride: JSON.stringify(buildAddOverrideForMember(group, user)),
            })
                .then(() => {
                    successes.push(group);
                    callbacks.onProgress(successes.length + failures.length, total);
                    return processNext();
                })
                .catch((reason: string) => {
                    failures.push({group, error: reason});
                    callbacks.onProgress(successes.length + failures.length, total);
                    return processNext();
                });
        };

        void processNext().then(() => {
            invalidateMembershipOverrideCaches();
            callbacks.onComplete(successes, failures);
        });
    }

    /** Numeric rank for override-column client sort (`ascending` controls no-override placement). */
    export function getMembershipOverrideSortRank(
        override: IOverride | null | undefined,
        ascending: boolean,
    ): number {
        if (!override) {
            return ascending ? NO_OVERRIDE_SORT_RANK : -1;
        }
        const suffix = getOverrideStatusSuffix(override);
        return MEMBERSHIP_OVERRIDE_SORT_RANK[suffix] ?? UNKNOWN_OVERRIDE_SORT_RANK;
    }

}
